/**
 * MultiPartBuilderPanel — Studio mode for building 6-10 min animated
 * documentaries around Agent Opus's 3-min script-to-video cap.
 *
 * Two steps in one panel:
 *
 *   1. **Split script** — paste the long script, we split it at sentence
 *      boundaries into 2.8-min chunks with a per-chunk COPY button so
 *      you can paste each one into Opus's script-to-video one at a time.
 *
 *   2. **Stitch parts** — after Opus produces an MP4 per chunk, upload
 *      each one back here in order. When all parts are uploaded, hit
 *      Stitch → we ffmpeg-concat them into a single seamless MP4 you
 *      can then publish through the normal Storylo flow.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Scissors, Copy, Check, Film, Upload, Trash2, Sparkles,
  Loader2, Download, X, ArrowRight, ChevronDown, ChevronUp, Play, Plus,
} from "lucide-react";
import ProductionsBoard from "@/ProductionsBoard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function makeSyntheticChunks(count) {
  return Array.from({ length: count }, (_, i) => ({
    index: i + 1,
    text: "",
    word_count: 0,
    char_count: 0,
    estimated_seconds: 0,
    _synthetic: true,
  }));
}

function makeSessionId() {
  // RFC-4122-ish uuid via crypto.randomUUID (falls back to timestamp).
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-4000-8000-000000000000`;
  }
}

export default function MultiPartBuilderPanel() {
  // Step 1 — script splitter
  const [script, setScript] = useState("");
  const [targetMin, setTargetMin] = useState(2.5);
  const [maxWords, setMaxWords] = useState(440);
  const [chunks, setChunks] = useState([]);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitError, setSplitError] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [sentIndices, setSentIndices] = useState([]); // chunks copied into Opus (persisted)
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);
  // "Skip to upload" mode — for when Opus MP4s already exist and the
  // user just needs upload slots, no script splitting required.
  const [skipCount, setSkipCount] = useState(3);
  const [showSkipInput, setShowSkipInput] = useState(false);
  // "My projects" dropdown — recent stitch sessions from the server.
  const [projects, setProjects] = useState([]);
  const [showProjects, setShowProjects] = useState(false);

  // Step 2 — video stitcher
  const [sessionId] = useState(() => {
    // Persist session so a hard-refresh doesn't lose uploaded parts.
    const KEY = "storylo_stitch_session";
    let s = window.localStorage.getItem(KEY);
    if (!s) {
      s = makeSessionId();
      window.localStorage.setItem(KEY, s);
    }
    return s;
  });
  const [uploadedParts, setUploadedParts] = useState([]);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({}); // {partIndex: {done, total, stage}}
  const [linkFormIdx, setLinkFormIdx] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [stitching, setStitching] = useState(false);
  const [stitchState, setStitchState] = useState(null); // {progress, stage}
  const [stitchResult, setStitchResult] = useState(null);
  const [stitchError, setStitchError] = useState("");
  // Publish-to-library state.
  const [publishTitle, setPublishTitle] = useState("");
  const [publishLanguage, setPublishLanguage] = useState("en");
  const [publishing, setPublishing] = useState(false);
  const [pubThumb, setPubThumb] = useState(null); // optional File for the thumbnail
  const [publishStage, setPublishStage] = useState("");
  const [publishBytes, setPublishBytes] = useState(0);
  const [showForceStitch, setShowForceStitch] = useState(false);
  const [published, setPublished] = useState(null); // {id, slug, public_url}
  // Optional taxonomy — carried straight into the published video doc
  // so the entry lands fully categorised in one click.
  const [pubMainCat, setPubMainCat] = useState("");
  const [pubCountry, setPubCountry] = useState("");
  const [pubYear, setPubYear] = useState("");
  const [pubLength, setPubLength] = useState("");
  const [pubTags, setPubTags] = useState("");
  const [pubLabels, setPubLabels] = useState([]);
  const [taxManifest, setTaxManifest] = useState(null);
  const [showTaxonomy, setShowTaxonomy] = useState(false);
  const fileInputRefs = useRef({});

  // Fetch the taxonomy manifest once so the Publish form has the same
  // dropdown options as the Categorise modal.
  useEffect(() => {
    axios.get(`${API}/taxonomy`).then(({ data }) => setTaxManifest(data));
  }, []);

  // Hydrate uploaded parts from server on mount. If parts already exist
  // (returning to a session after leaving), auto-create enough upload
  // slots to show them all — previously the user came back to 0 slots
  // and the Skip default of 3 capped them below their real part count.
  useEffect(() => {
    axios.get(`${API}/multi-part/session/${sessionId}`)
      .then(({ data }) => {
        const parts = data.parts || [];
        setUploadedParts(parts);
        setSentIndices(data.copied_chunks || []);
        const savedChunks = data.chunks || [];
        if (savedChunks.length) {
          // Server-saved script chunks (from a previous visit) — restore
          // them so the Copy-into-Opus buttons come back after a browser
          // shutdown mid-project.
          setChunks((prev) => (prev.length ? prev : savedChunks));
          if (data.script_text) {
            setScript((prev) => prev || data.script_text);
          }
        } else if (parts.length) {
          const maxIdx = Math.max(...parts.map((p) => p.part_index));
          setChunks((prev) =>
            prev.length >= maxIdx ? prev : makeSyntheticChunks(maxIdx)
          );
        }
      })
      .catch(() => {});
  }, [sessionId]);

  const doSplit = async () => {
    setSplitError("");
    setSplitLoading(true);
    setChunks([]);
    try {
      const { data } = await axios.post(`${API}/scripts/split-for-chunks`, {
        text: script,
        target_minutes: Number(targetMin) || 2.5,
        max_words_per_chunk: Number(maxWords) || 440,
        session_id: sessionId,
      });
      setChunks(data.chunks || []);
    } catch (e) {
      setSplitError(e?.response?.data?.detail || e.message);
    } finally {
      setSplitLoading(false);
    }
  };

  // Skip-to-upload — user already has Opus MP4s and wants direct upload
  // slots without splitting a script. Creates N synthetic "chunks" with
  // empty text so Step 2 renders N upload rows.
  const skipToUpload = (n) => {
    const count = Math.max(1, Math.min(20, Number(n) || 1));
    setChunks(makeSyntheticChunks(count));
    setShowSkipInput(false);
    setSplitError("");
  };

  // "Add another part" — extend the slot list by one (works for both
  // script-split and skip sessions; new slot is a synthetic chunk).
  const addSlot = () => {
    setChunks((prev) => [
      ...prev,
      {
        index: prev.length + 1,
        text: "",
        word_count: 0,
        char_count: 0,
        estimated_seconds: 0,
        _synthetic: true,
      },
    ]);
  };

  const copyChunk = async (idx, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 1500);
    } catch (e) {

      alert("Couldn't copy to clipboard: " + e.message);
    }
    // Persist "sent to Opus" for progress tracking (even if clipboard failed).
    const fd = new FormData();
    fd.append("part_index", String(idx));
    axios.post(`${API}/multi-part/session/${sessionId}/chunk-copied`, fd).catch(() => {});
    setSentIndices((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
  };

  const totalDurationLabel = useMemo(() => {
    if (!chunks.length) return "";
    const secs = chunks.reduce((a, c) => a + c.estimated_seconds, 0);
    const m = Math.floor(secs / 60);
    const s = Math.round(secs - m * 60);
    return `${m}m ${String(s).padStart(2, "0")}s`;
  }, [chunks]);

  const importFromUrl = async (partIndex) => {
    const url = linkUrl.trim();
    setLinkFormIdx(null);
    setLinkUrl("");
    setUploadingIdx(partIndex);
    setUploadProgress((p) => ({
      ...p, [partIndex]: { done: 50, total: 100, stage: "importing" },
    }));
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("part_index", String(partIndex));
      fd.append("url", url);
      await axios.post(`${API}/multi-part/import-from-url`, fd, { timeout: 30_000 });
      // Server downloads + stores in the background — poll until the
      // part appears assembled or reports an error.
      const POLL_MAX = 120; // 120 × 3 s = 6 min
      let finished = false;
      for (let i = 0; i < POLL_MAX; i += 1) {
        await new Promise((r) => setTimeout(r, 3000));
        let data = null;
        try {
          ({ data } = await axios.get(
            `${API}/multi-part/session/${sessionId}`, { timeout: 30_000 }
          ));
        } catch {
          continue;
        }
        if ((data.parts || []).some((p) => p.part_index === partIndex)) {
          setUploadedParts(data.parts || []);
          finished = true;
          break;
        }
        const pend = (data.pending || []).find((p) => p.part_index === partIndex);
        if (pend?.assembly_error) {
          throw new Error(pend.assembly_error);
        }
      }
      if (!finished) {
        throw new Error("Import timed out — check the link is public and points at the MP4, then try again.");
      }
    } catch (e) {

      alert(e?.response?.data?.detail || e.message || "Import failed");
    } finally {
      setUploadingIdx(null);
      setUploadProgress((p) => {
        const n = { ...p };
        delete n[partIndex];
        return n;
      });
    }
  };

  const uploadPart = async (partIndex, file) => {
    if (!file) return;
    setUploadingIdx(partIndex);
    try {
      // Small files (<4 MB) go via the single-request path — cheaper.
      // Everything larger uses the chunked endpoint so we stay under
      // Cloudflare's ~100 MB per-request cap and get retry-per-chunk
      // resilience for the big Opus MP4s (often 80-200 MB).
      const CHUNK_SIZE = 4 * 1024 * 1024;
      if (file.size <= CHUNK_SIZE) {
        setUploadProgress((p) => ({ ...p, [partIndex]: { done: 0, total: 1, stage: "uploading" } }));
        const fd = new FormData();
        fd.append("session_id", sessionId);
        fd.append("part_index", String(partIndex));
        fd.append("file", file);
        await axios.post(`${API}/multi-part/upload-part`, fd, { timeout: 120_000 });
      } else {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        // RESUME: if a previous attempt of this exact file (same name,
        // same chunk count) was interrupted, skip the chunks the server
        // already has instead of starting from zero.
        let have = new Set();
        try {
          const { data: sess } = await axios.get(
            `${API}/multi-part/session/${sessionId}`, { timeout: 20_000 }
          );
          const pend = (sess.pending || []).find(
            (p) =>
              p.part_index === partIndex &&
              p.total_chunks === totalChunks &&
              p.filename === file.name &&
              !p.assembly_error &&
              !p.assembling
          );
          // Chunk 0 must be among the received ones — resending it
          // resets server state, so a resume without it is impossible.
          if (pend && (pend.chunks_received_indices || []).includes(0)) {
            have = new Set(pend.chunks_received_indices);
          }
        } catch { /* no resume info — fresh upload */ }

        let done = have.size;
        setUploadProgress((p) => ({
          ...p,
          [partIndex]: { done, total: totalChunks, stage: "uploading" },
        }));
        const bump = () => {
          done += 1;
          setUploadProgress((p) => ({
            ...p,
            [partIndex]: { done, total: totalChunks, stage: "uploading" },
          }));
        };
        const sendChunk = async (i) => {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const slice = file.slice(start, end);
          const fd = new FormData();
          fd.append("session_id", sessionId);
          fd.append("part_index", String(partIndex));
          fd.append("chunk_index", String(i));
          fd.append("total_chunks", String(totalChunks));
          fd.append("filename", file.name);
          fd.append("file", slice, `${file.name}.part${i}`);
          // 4 attempts with exponential backoff — rides out transient
          // Cloudflare 502/520 blips that instant-retries can't.
          let lastErr = null;
          for (let attempt = 0; attempt < 4; attempt += 1) {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, 1500 * 2 ** (attempt - 1)));
            }
            try {
              await axios.post(
                `${API}/multi-part/upload-part-chunk`,
                fd,
                { timeout: 180_000 }
              );
              lastErr = null;
              break;
            } catch (err) {
              lastErr = err;
            }
          }
          if (lastErr) throw lastErr;
          bump();
        };
        // Chunk 0 must land first on a FRESH upload — it resets
        // server-side state. On resume it's already there, skip it.
        if (!have.has(0)) {
          await sendChunk(0);
        }
        const queue = [];
        for (let i = 1; i < totalChunks; i += 1) {
          if (!have.has(i)) queue.push(i);
        }
        const CONCURRENCY = 3;
        const workers = Array.from(
          { length: Math.min(CONCURRENCY, queue.length) },
          async () => {
            while (queue.length) {
              const i = queue.shift();
              await sendChunk(i);
            }
          }
        );
        await Promise.all(workers);

        // All chunks are in — the server merges + verifies + stores the
        // MP4 in the BACKGROUND (a held-open request would be killed by
        // Cloudflare after ~100 s on big parts). Poll until the part
        // reports assembled, or surfaces an assembly error.
        setUploadProgress((p) => ({
          ...p,
          [partIndex]: { done: totalChunks, total: totalChunks, stage: "finalising" },
        }));
        const POLL_MAX = 200; // 200 × 3 s = 10 min
        let finished = false;
        for (let i = 0; i < POLL_MAX; i += 1) {
          await new Promise((r) => setTimeout(r, 3000));
          let data = null;
          try {
            ({ data } = await axios.get(
              `${API}/multi-part/session/${sessionId}`, { timeout: 30_000 }
            ));
          } catch {
            continue; // transient poll error — assembly continues server-side
          }
          if ((data.parts || []).some((p) => p.part_index === partIndex)) {
            setUploadedParts(data.parts || []);
            finished = true;
            break;
          }
          const pend = (data.pending || []).find((p) => p.part_index === partIndex);
          if (pend?.assembly_error) {
            throw new Error(`Part ${partIndex} failed to finalise: ${pend.assembly_error}`);
          }
        }
        if (!finished) {
          throw new Error(
            "Timed out waiting for this part to finalise. Refresh the page to check if it appeared — otherwise re-upload it."
          );
        }
        return;
      }
      const { data } = await axios.get(`${API}/multi-part/session/${sessionId}`);
      setUploadedParts(data.parts || []);
    } catch (e) {

      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      alert(
        `${msg}\n\nTip: click "Upload MP4" again and pick the same file — the upload resumes from where it stopped instead of starting over.`
      );
    } finally {
      setUploadingIdx(null);
      setUploadProgress((p) => {
        const n = { ...p };
        delete n[partIndex];
        return n;
      });
    }
  };

  const deletePart = async (partIndex) => {
    try {
      await axios.delete(`${API}/multi-part/part/${sessionId}/${partIndex}`);
      const { data } = await axios.get(`${API}/multi-part/session/${sessionId}`);
      setUploadedParts(data.parts || []);
    } catch (e) {

      alert(e.message);
    }
  };

  const runStitch = async (force = false) => {
    if (uploadedParts.length < 2) {
      setStitchError("Upload at least 2 parts before stitching");
      return;
    }
    setStitchError("");
    setShowForceStitch(false);
    setStitching(true);
    setStitchResult(null);
    setStitchState({ progress: 0, stage: "queued" });
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      if (force) fd.append("force", "true");
      const { data: start } = await axios.post(
        `${API}/multi-part/stitch/start`,
        fd,
        { timeout: 30_000 }
      );
      const jid = start.job_id;
      // Poll until the job reaches a terminal state — up to 3 hours.
      // Long documentaries (35min+) need well over the old 30-min cap;
      // each poll is short & CF-safe, so long waits cost nothing. We
      // also watch the job's heartbeat (updated_at): if the server
      // stops updating for 10+ min the job is genuinely dead.
      const MAX_TICKS = 5400; // 5400 × 2s = 3 h
      let consecutiveErrors = 0;
      let lastHeartbeat = Date.now();
      let lastSeenUpdate = null;
      for (let i = 0; i < MAX_TICKS; i += 1) {

        await new Promise((r) => setTimeout(r, 2000));

        try {
          const { data: st } = await axios.get(
            `${API}/multi-part/stitch/${jid}`,
            { timeout: 30_000 }
          );
          consecutiveErrors = 0;
          // Heartbeat = the server-side updated_at CHANGING between
          // polls. We deliberately never compare server time against
          // the browser clock — timezone offsets (e.g. UK BST vs UTC)
          // and clock skew made that misread live jobs as stalled.
          if (st.updated_at && st.updated_at !== lastSeenUpdate) {
            lastSeenUpdate = st.updated_at;
            lastHeartbeat = Date.now();
          }
          setStitchState({ progress: st.progress || 0, stage: st.stage || "processing" });
          if (st.status === "done") {
            setStitchResult(st);
            return;
          }
          if (st.status === "failed") {
            setStitchError(st.error || "Stitch failed");
            return;
          }
          if (Date.now() - lastHeartbeat > 10 * 60_000) {
            setStitchError(
              "The stitch job stopped responding (no progress for 10 minutes). Click Stitch again to resume or restart it."
            );
            return;
          }
        } catch (pollErr) {
          // Transient poll error (timeout, brief 5xx, network blip).
          // The stitch job continues on the server — keep polling.
          // Only give up after ~5 min of continuous failures (150 × 2s).
          consecutiveErrors += 1;
          if (consecutiveErrors <= 150) {
            setStitchState((prev) => ({
              progress: prev?.progress || 0,
              stage: `polling… (${consecutiveErrors})`,
            }));
            continue;
          }
          throw pollErr;
        }
      }
      setStitchError("Stitching is taking unusually long. The file may still be building — click Stitch again to resume watching it.");
    } catch (e) {
      if (e?.response?.status === 409) {
        setShowForceStitch(true);
      }
      setStitchError(e?.response?.data?.detail || e.message);
    } finally {
      setStitching(false);
      setStitchState(null);
    }
  };

  const toggleProjects = async () => {
    if (!showProjects) {
      try {
        const { data } = await axios.get(`${API}/multi-part/sessions`);
        setProjects(data.sessions || []);
      } catch (_e) {
        setProjects([]);
      }
    }
    setShowProjects((v) => !v);
  };

  const removeProject = async (e, sid) => {
    e.stopPropagation();
    const ok = window.confirm(
      "Remove this project from the list?\n\nThis deletes its saved script, chunks and any uploaded parts. Videos already stitched & published are NOT affected."
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/multi-part/session/${sid}`);
    } catch (_e) { /* best-effort */ }
    setProjects((prev) => prev.filter((p) => p.session_id !== sid));
    if (sid === sessionId) {
      window.localStorage.removeItem("storylo_stitch_session");
      window.location.reload();
    }
  };

  const switchToProject = (sid) => {
    if (sid === sessionId) {
      setShowProjects(false);
      return;
    }
    window.localStorage.setItem("storylo_stitch_session", sid);
    window.localStorage.setItem("storylo_studio_mode", "multi_part");
    window.location.reload(); // remount restores that session's chunks + parts
  };

  const deleteProject = async () => {
    const ok = window.confirm(
      "Delete this multi-part project?\n\nThis removes the saved script, all its Opus chunks AND any uploaded MP4 parts. This cannot be undone."
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/multi-part/session/${sessionId}`);
    } catch (_e) { /* server cleanup is best-effort — still reset */ }
    setScript("");
    setChunks([]);
    setUploadedParts([]);
    setStitchResult(null);
    setStitchError("");
    window.localStorage.removeItem("storylo_stitch_session");
    window.localStorage.setItem("storylo_studio_mode", "multi_part");
    window.location.reload(); // regenerates a fresh sessionId on mount
  };

  const startNewProject = () => {
    // Wipe local state + generate a new session id so uploads for the
    // NEXT project don't mix with the previous one.
    setScript("");
    setChunks([]);
    setUploadedParts([]);
    setStitchResult(null);
    setStitchError("");
    window.localStorage.removeItem("storylo_stitch_session");
    window.localStorage.setItem("storylo_studio_mode", "multi_part");
    window.location.reload(); // simplest — regenerates the sessionId on mount
  };

  const uploadedIndices = new Set(uploadedParts.map((p) => p.part_index));
  // A chunk planned by the script-splitter but never uploaded is only a
  // problem if it sits BETWEEN uploaded parts (a gap would splice the
  // story out of order). Trailing chunks the creator decided not to use
  // (e.g. split said 3 but the video only needs 2) must NOT block the
  // stitch — the backend happily stitches any 2+ contiguous parts.
  const maxUploadedIdx = uploadedParts.length
    ? Math.max(...uploadedParts.map((p) => p.part_index))
    : 0;
  const plannedIndices = chunks.map((c) => c.index);
  const gapIndices = plannedIndices.filter(
    (i) => i < maxUploadedIdx && !uploadedIndices.has(i)
  );
  const trailingUnused = plannedIndices.filter(
    (i) => i > maxUploadedIdx
  );

  return (
    <div className="space-y-6" data-testid="multi-part-panel">
      {/* ============================= STEP 1 ============================= */}
      <div className="panel p-6 md:p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow mb-1 flex items-center gap-2">
              <Scissors className="w-3.5 h-3.5" />
              Step 1 · Split script for Opus
            </div>
            <div className="font-display text-xl leading-snug">
              Break a long script into 3-min chunks
            </div>
            <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed max-w-xl">
              Paste your full narration script. We&apos;ll split it at sentence
              boundaries into chunks each ~2.8 min long (default) — safely
              under Opus&apos;s 3-min script-to-video cap. Copy each chunk
              into Opus one at a time.
            </div>
          </div>
          <div className="relative shrink-0 flex items-center gap-2">
            <button
              onClick={startNewProject}
              className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-[color:var(--accent)]/50 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 inline-flex items-center gap-1.5"
              data-testid="new-project-btn"
              title="Start a fresh multi-part project — your current one stays saved under My projects"
            >
              <Plus className="w-3 h-3" /> New project
            </button>
            <button
              onClick={toggleProjects}
              className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1.5"
              data-testid="my-projects-btn"
              title="Your recent multi-part projects — switch between them"
            >
              <Film className="w-3 h-3" /> My projects
              <ChevronDown className={`w-3 h-3 transition-transform ${showProjects ? "rotate-180" : ""}`} />
            </button>
            {showProjects && (
              <div
                className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-white/10 bg-[#14120e] shadow-2xl z-30 p-1.5"
                data-testid="my-projects-dropdown"
              >
                {projects.length === 0 && (
                  <div className="text-xs text-[color:var(--muted)] p-3">
                    No saved projects yet — split a script or upload parts
                    and it will appear here.
                  </div>
                )}
                {projects.length > 0 && (
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[color:var(--muted)] px-2.5 pt-2 pb-1">
                    Click a project to open it
                  </div>
                )}
                {projects.map((p) => (
                  <div
                    key={p.session_id}
                    onClick={() => switchToProject(p.session_id)}
                    role="button"
                    tabIndex={0}
                    className={`w-full text-left p-2.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer flex items-start gap-2 ${
                      p.session_id === sessionId ? "bg-white/[0.06] border border-[color:var(--accent)]/40" : ""
                    }`}
                    data-testid={`project-item-${p.session_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate">
                        {p.title}
                        {p.session_id === sessionId && (
                          <span className="text-[color:var(--accent)] ml-1.5">· current</span>
                        )}
                      </div>
                      <div className="text-[10px] text-[color:var(--muted)] mt-0.5 flex gap-2.5">
                        <span>{p.chunk_count} chunks</span>
                        <span>{p.parts_uploaded} parts uploaded</span>
                        {p.updated_at && (
                          <span>{new Date(p.updated_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => removeProject(e, p.session_id)}
                      className="shrink-0 p-1 rounded text-[color:var(--muted)] hover:text-rose-300 hover:bg-rose-500/10"
                      data-testid={`project-remove-${p.session_id}`}
                      title="Remove this project (deletes its saved script, chunks and parts)"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-3 items-end">
          <div>
            <label className="label-eyebrow mb-1 block">
              Target minutes per chunk
            </label>
            <input
              type="number"
              value={targetMin}
              step="0.1"
              min="1"
              max="5"
              onChange={(e) => setTargetMin(e.target.value)}
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
              data-testid="split-target-min"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1 block">
              Max words per chunk (Opus cap)
            </label>
            <input
              type="number"
              value={maxWords}
              step="10"
              min="100"
              max="1000"
              onChange={(e) => setMaxWords(e.target.value)}
              className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
              data-testid="split-max-words"
              title="Hard cap — no chunk will exceed this word count, even if it means the chunk is shorter than the target minutes."
            />
          </div>
          <button
            onClick={doSplit}
            disabled={splitLoading || script.trim().length < 20}
            className="btn-primary text-sm inline-flex items-center gap-2 px-5 py-2.5 whitespace-nowrap disabled:opacity-40"
            data-testid="split-run"
          >
            {splitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Split into chunks
          </button>
        </div>

        {/* Skip-to-upload shortcut — for when Opus MP4s are already on hand */}
        <div className="mb-3">
          {!showSkipInput ? (
            <button
              onClick={() => setShowSkipInput(true)}
              className="text-xs text-[color:var(--muted)] hover:text-[color:var(--accent)] underline underline-offset-4 decoration-dotted inline-flex items-center gap-1.5"
              data-testid="skip-to-upload-toggle"
            >
              <ArrowRight className="w-3 h-3" />
              Skip — I already have my Opus MP4s
            </button>
          ) : (
            <div className="panel p-3 flex flex-wrap items-center gap-3 border-white/15">
              <div className="text-xs text-[color:var(--muted)]">
                How many parts do you have?
              </div>
              <input
                type="number"
                min="1"
                max="20"
                value={skipCount}
                onChange={(e) => setSkipCount(e.target.value)}
                className="w-20 bg-transparent border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:border-[color:var(--accent)] outline-none"
                data-testid="skip-count-input"
              />
              <button
                onClick={() => skipToUpload(skipCount)}
                className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1"
                data-testid="skip-to-upload-confirm"
              >
                <ArrowRight className="w-3 h-3" />
                Open upload slots
              </button>
              <button
                onClick={() => setShowSkipInput(false)}
                className="text-[10px] text-[color:var(--muted)] hover:text-white"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          rows={8}
          placeholder="Paste your full narration script here… (as long as you like)"
          className="input-base w-full px-4 py-3 leading-relaxed resize-y"
          data-testid="split-script"
        />

        {splitError && (
          <div className="mt-3 panel p-3 border-red-500/40 text-red-300 text-sm">
            {splitError}
          </div>
        )}

        {chunks.length > 0 && !chunks[0]._synthetic && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-3 text-xs">
              <div className="text-[color:var(--muted)]">
                <span className="text-[color:var(--accent)] font-medium">
                  {chunks.length} chunks
                </span>{" "}
                · estimated total {totalDurationLabel}
              </div>
              <div className="flex items-center gap-3 text-[color:var(--muted)]">
                <span>Copy each into Opus in order · then upload the MP4s below</span>
                <button
                  onClick={deleteProject}
                  className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-md border border-white/15 text-rose-300 hover:border-rose-400 hover:bg-rose-500/10 inline-flex items-center gap-1"
                  data-testid="delete-project-btn"
                  title="Delete this project — removes the saved script, all chunks and uploaded parts"
                >
                  <Trash2 className="w-3 h-3" /> Delete project
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {chunks.map((c) => {
                const uploaded = uploadedIndices.has(c.index);
                const expanded = expandedIdx === c.index;
                return (
                  <div
                    key={c.index}
                    className={`panel p-3 border ${
                      uploaded
                        ? "border-emerald-500/40 bg-emerald-500/[0.04]"
                        : "border-white/10"
                    }`}
                    data-testid={`chunk-${c.index}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 shrink-0 font-display text-[color:var(--accent)] text-lg">
                        {String(c.index).padStart(2, "0")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-xs leading-relaxed ${
                            expanded ? "" : "line-clamp-2"
                          }`}
                        >
                          {c.text}
                        </div>
                        <div className="text-[10px] text-[color:var(--muted)] mt-1.5 flex flex-wrap gap-x-3">
                          <span>{c.word_count} words</span>
                          <span>{c.char_count} chars</span>
                          <span>~{Math.floor(c.estimated_seconds / 60)}m {String(Math.round(c.estimated_seconds % 60)).padStart(2, "0")}s</span>
                          {uploaded ? (
                            <span className="text-emerald-400">
                              ✓ MP4 uploaded
                            </span>
                          ) : sentIndices.includes(c.index) ? (
                            <span className="text-amber-400" data-testid={`chunk-owed-${c.index}`}>
                              ⏳ sent to Opus — MP4 owed
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={() => copyChunk(c.index, c.text)}
                          className={`text-[11px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-md border transition-colors inline-flex items-center gap-1 ${
                            copiedIdx === c.index
                              ? "border-emerald-500/50 text-emerald-400"
                              : "border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                          }`}
                          data-testid={`chunk-copy-${c.index}`}
                        >
                          {copiedIdx === c.index ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {copiedIdx === c.index ? "Copied" : "Copy"}
                        </button>
                        <button
                          onClick={() =>
                            setExpandedIdx(expanded ? null : c.index)
                          }
                          className="text-[10px] text-[color:var(--muted)] hover:text-white inline-flex items-center gap-1 justify-center"
                        >
                          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {expanded ? "Less" : "More"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ====================== MY PRODUCTIONS ====================== */}
      <ProductionsBoard
        currentSessionId={sessionId}
        onOpenProject={switchToProject}
        onOpenResult={(job) => {
          setStitchResult(job);
          setStitchError("");
          setPublished(null);
          setTimeout(
            () => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }),
            150
          );
        }}
      />

      {/* ============================= STEP 2 ============================= */}
      {chunks.length > 0 && (
        <div className="panel p-6 md:p-8">
          <div className="mb-4">
            <div className="label-eyebrow mb-1 flex items-center gap-2">
              <Film className="w-3.5 h-3.5" />
              Step 2 · Upload the Opus MP4s in order
            </div>
            <div className="font-display text-xl leading-snug">
              Attach each animated part
            </div>
            <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed max-w-xl">
              For every chunk, drop the MP4 Opus produced under the matching
              slot below. Parts don&apos;t need to be identical size or bitrate —
              we normalise everything to 1920×1080 during stitching.
            </div>
          </div>

          <div className="space-y-2">
            {chunks.map((c) => {
              const uploaded = uploadedParts.find((p) => p.part_index === c.index);
              const busy = uploadingIdx === c.index;
              return (
                <div
                  key={c.index}
                  className={`panel p-3 ${
                    uploaded ? "border-emerald-500/40 bg-emerald-500/[0.04]" : ""
                  }`}
                  data-testid={`upload-slot-${c.index}`}
                >
                  <div className="flex items-center gap-3">
                  <div className="w-10 shrink-0 font-display text-[color:var(--accent)] text-lg text-center">
                    {String(c.index).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0">
                    {uploaded ? (
                      <div className="text-sm">
                        <span className="text-emerald-400">✓ {uploaded.filename}</span>
                        <span className="text-xs text-[color:var(--muted)] ml-2">
                          {(uploaded.size_bytes / 1_000_000).toFixed(1)} MB
                        </span>
                      </div>
                    ) : (
                      <div className="text-xs text-[color:var(--muted)]">
                        Upload the Opus MP4 for chunk {c.index}
                      </div>
                    )}
                  </div>
                  <input
                    ref={(el) => {
                      fileInputRefs.current[c.index] = el;
                    }}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm"
                    className="hidden"
                    onChange={(e) => uploadPart(c.index, e.target.files?.[0])}
                  />
                  {uploaded ? (
                    <>
                      <button
                        onClick={() =>
                          setPreviewIdx(previewIdx === c.index ? null : c.index)
                        }
                        className="text-[11px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1"
                        data-testid={`upload-preview-${c.index}`}
                        title="Watch this part before stitching — check it's the right video"
                      >
                        <Play className="w-3 h-3" />
                        {previewIdx === c.index ? "Hide" : "Preview"}
                      </button>
                      <button
                        onClick={() => deletePart(c.index)}
                        className="text-[11px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md border border-white/15 text-rose-300 hover:border-rose-400 hover:bg-rose-500/10 inline-flex items-center gap-1"
                        data-testid={`upload-remove-${c.index}`}
                      >
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => fileInputRefs.current[c.index]?.click()}
                        disabled={busy}
                        className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`upload-btn-${c.index}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {busy ? "Working…" : "Upload MP4"}
                      </button>
                      <button
                        onClick={() => setLinkFormIdx(linkFormIdx === c.index ? null : c.index)}
                        disabled={busy}
                        className="text-[11px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-md border border-white/15 text-[color:var(--muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] disabled:opacity-40"
                        data-testid={`import-link-toggle-${c.index}`}
                        title="Paste a Google Drive / Dropbox / direct link — the server downloads it at high speed (no browser upload)"
                      >
                        Paste a link
                      </button>
                    </div>
                  )}
                  </div>
                  {linkFormIdx === c.index && !uploaded && !busy && (
                    <div className="mt-2.5 pl-[52px] flex items-center gap-2" data-testid={`import-link-form-${c.index}`}>
                      <input
                        type="text"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Paste a Google Drive / Dropbox / direct MP4 link…"
                        className="flex-1 bg-transparent border border-white/10 rounded-lg px-3 py-1.5 text-xs focus:border-[color:var(--accent)] outline-none"
                        data-testid={`import-link-input-${c.index}`}
                      />
                      <button
                        onClick={() => importFromUrl(c.index)}
                        disabled={!linkUrl.trim().startsWith("http")}
                        className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-[color:var(--accent)]/50 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/10 disabled:opacity-40"
                        data-testid={`import-link-go-${c.index}`}
                      >
                        Import
                      </button>
                    </div>
                  )}
                  {busy && uploadProgress[c.index] && (
                    <div className="mt-2.5 pl-[52px]" data-testid={`upload-progress-${c.index}`}>
                      <div className="flex items-center justify-between text-[10px] text-[color:var(--muted)] mb-1">
                        <span>
                          {uploadProgress[c.index].stage === "importing"
                            ? "Importing from link — server is downloading & storing the MP4…"
                            : uploadProgress[c.index].stage === "finalising"
                            ? "Finalising — verifying & storing the MP4…"
                            : `Uploading · ${uploadProgress[c.index].done}/${uploadProgress[c.index].total} chunks (3 in parallel)`}
                        </span>
                        <span className="text-[color:var(--accent)]">
                          {Math.min(99, Math.round((uploadProgress[c.index].done / uploadProgress[c.index].total) * 100))}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-[width] duration-300 ${
                            uploadProgress[c.index].stage === "finalising"
                              ? "bg-emerald-500 animate-pulse"
                              : "bg-[color:var(--accent)]"
                          }`}
                          style={{ width: `${Math.min(99, (uploadProgress[c.index].done / uploadProgress[c.index].total) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {uploaded && previewIdx === c.index && (
                    <video
                      controls
                      preload="metadata"
                      className="mt-3 w-full max-h-80 rounded-lg bg-black"
                      src={`${API}/multi-part/part/${sessionId}/${c.index}/preview`}
                      data-testid={`part-preview-video-${c.index}`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={addSlot}
            className="mt-3 text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-dashed border-white/20 text-[color:var(--muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1.5 transition-colors"
            data-testid="add-part-slot"
            title="Need more parts than planned? Add another upload slot"
          >
            <Upload className="w-3 h-3" /> Add another part
          </button>

          {stitchError && (
            <div className="mt-4 panel p-3 border-red-500/40 text-red-300 text-sm">
              {stitchError}
              {showForceStitch && (
                <button
                  onClick={() => runStitch(true)}
                  className="mt-2 block text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-amber-400/60 text-amber-300 hover:bg-amber-400/10"
                  data-testid="stitch-force"
                >
                  Stitch anyway (I know it's a duplicate)
                </button>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => runStitch()}
              disabled={stitching || uploadedParts.length < 2 || gapIndices.length > 0}
              className="btn-primary text-sm inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-40"
              data-testid="stitch-run"
            >
              {stitching ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Stitching…
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4" />
                  Stitch into one MP4
                </>
              )}
            </button>
            {stitching && stitchState && (
              <div className="text-xs text-[color:var(--muted)]">
                {stitchState.stage.replace(/_/g, " ")}… {stitchState.progress}%
              </div>
            )}
            {gapIndices.length > 0 && !stitching && (
              <div className="text-xs text-amber-300" data-testid="stitch-gap-warning">
                Gap in sequence — upload chunk{gapIndices.length > 1 ? "s" : ""}{" "}
                {gapIndices.join(", ")} (or remove the later parts) before stitching.
              </div>
            )}
            {gapIndices.length === 0 && trailingUnused.length > 0 &&
              uploadedParts.length >= 2 && !stitching && (
              <div className="text-xs text-[color:var(--muted)]" data-testid="stitch-trailing-note">
                Chunk{trailingUnused.length > 1 ? "s" : ""} {trailingUnused.join(", ")} from
                your split {trailingUnused.length > 1 ? "were" : "was"} not uploaded — the{" "}
                {uploadedParts.length} part{uploadedParts.length > 1 ? "s" : ""} you added
                will be stitched.
              </div>
            )}
          </div>

          {stitching && stitchState && stitchState.progress > 0 && (
            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full bg-[color:var(--accent)] transition-all duration-500 ease-out"
                style={{ width: `${stitchState.progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* ============================= RESULT ============================= */}
      {stitchResult && (
        <div className="panel p-6" data-testid="stitch-result">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="label-eyebrow mb-1">Documentary ready</div>
              <div className="font-display text-lg">
                Your {stitchResult.part_count}-part video is stitched
              </div>
              <div className="text-xs text-[color:var(--muted)] mt-1">
                <Film className="w-3 h-3 inline mr-1" />
                {Math.floor((stitchResult.duration_seconds || 0) / 60)}m{" "}
                {String(Math.round((stitchResult.duration_seconds || 0) % 60)).padStart(2, "0")}s
                {" · "}
                {(stitchResult.size_bytes / 1_000_000).toFixed(1)} MB
              </div>
            </div>
            <button
              onClick={() => setStitchResult(null)}
              className="text-[color:var(--muted)] hover:text-white"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <video
            src={`${BACKEND_URL}${stitchResult.output_url}`}
            controls
            className="w-full rounded-lg mb-3"
            data-testid="stitch-preview"
          />

          {/* -------- Publish to library (auto-publish) -------- */}
          {!published ? (
            <div className="mt-2 mb-4 panel p-4 border-[color:var(--accent)]/30">
              <div className="label-eyebrow mb-2">Publish straight to your library</div>
              <div className="text-xs text-[color:var(--muted)] mb-3 leading-relaxed">
                Adds this documentary to storylo.co.uk with its script, RSS feed
                and Spotify — no re-upload. Give it a title and pick the narration
                language.
              </div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-3">
                <input
                  type="text"
                  value={publishTitle}
                  onChange={(e) => setPublishTitle(e.target.value)}
                  placeholder="Story title (e.g. Balkans-10-…)"
                  className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                  data-testid="publish-title"
                />
                <select
                  value={publishLanguage}
                  onChange={(e) => setPublishLanguage(e.target.value)}
                  className="bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                  data-testid="publish-language"
                >
                  {[
                    ["en", "English"], ["de", "German"], ["fr", "French"],
                    ["es", "Spanish"], ["it", "Italian"], ["el", "Greek"],
                    ["tr", "Turkish"], ["ar", "Arabic"], ["nl", "Dutch"],
                    ["ru", "Russian"], ["zh", "Chinese"], ["hi", "Hindi"],
                  ].map(([c, n]) => (
                    <option key={c} value={c} className="bg-black">{n}</option>
                  ))}
                </select>
              </div>

              {/* Optional taxonomy — collapsed by default. */}
              <button
                type="button"
                onClick={() => setShowTaxonomy((v) => !v)}
                className="text-[11px] uppercase tracking-[0.15em] text-[color:var(--muted)] hover:text-white inline-flex items-center gap-1 mb-3"
                data-testid="publish-taxonomy-toggle"
              >
                {showTaxonomy ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Categorise on publish
                {(pubMainCat || pubCountry || pubYear || pubTags || pubLabels.length > 0) && (
                  <span className="text-[color:var(--accent)]">· ready</span>
                )}
              </button>

              {showTaxonomy && taxManifest && (
                <div className="space-y-3 mb-4 pl-2 border-l-2 border-[color:var(--accent)]/30" data-testid="publish-taxonomy-panel">
                  <div>
                    <label className="label-eyebrow mb-1 block">Main category</label>
                    <select
                      value={pubMainCat}
                      onChange={(e) => setPubMainCat(e.target.value)}
                      className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                      data-testid="publish-main-category"
                    >
                      <option value="" className="bg-black">— pick a category —</option>
                      {taxManifest.main_categories.map((c) => (
                        <option key={c.slug} value={c.slug} className="bg-black">{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="label-eyebrow mb-1 block">Country / region</label>
                      <input
                        type="text"
                        list="pub-country-suggest"
                        value={pubCountry}
                        onChange={(e) => setPubCountry(e.target.value)}
                        placeholder="e.g. Balkans"
                        className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                        data-testid="publish-country"
                      />
                      <datalist id="pub-country-suggest">
                        {taxManifest.common_countries.map((c) => (<option key={c} value={c} />))}
                      </datalist>
                    </div>
                    <div>
                      <label className="label-eyebrow mb-1 block">Year</label>
                      <input
                        type="number"
                        value={pubYear}
                        onChange={(e) => setPubYear(e.target.value)}
                        placeholder="e.g. 1990"
                        className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                        data-testid="publish-year"
                      />
                    </div>
                    <div>
                      <label className="label-eyebrow mb-1 block">Length</label>
                      <select
                        value={pubLength}
                        onChange={(e) => setPubLength(e.target.value)}
                        className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                        data-testid="publish-length"
                      >
                        <option value="" className="bg-black">— auto —</option>
                        {taxManifest.story_lengths.map((L) => (
                          <option key={L.slug} value={L.slug} className="bg-black">
                            {L.label} ({L.hint})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="label-eyebrow mb-1 block">Labels</label>
                    <div className="flex flex-wrap gap-1.5">
                      {taxManifest.story_labels.map((L) => {
                        const on = pubLabels.includes(L.slug);
                        return (
                          <button
                            key={L.slug}
                            type="button"
                            onClick={() => setPubLabels((s) =>
                              on ? s.filter((x) => x !== L.slug) : [...s, L.slug]
                            )}
                            className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border transition-colors"
                            style={{
                              borderColor: on ? L.color : "rgba(255,255,255,0.12)",
                              background: on ? `${L.color}22` : "transparent",
                              color: on ? L.color : "inherit",
                            }}
                            data-testid={`publish-label-${L.slug}`}
                          >
                            {on && "✓ "}
                            {L.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="label-eyebrow mb-1 block">Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={pubTags}
                      onChange={(e) => setPubTags(e.target.value)}
                      placeholder="e.g. balkans, war, yugoslavia"
                      className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                      data-testid="publish-tags"
                    />
                  </div>
                </div>
              )}
              <div className="mb-4">
                <label className="label-eyebrow mb-1 block">
                  Thumbnail
                  <span className="normal-case tracking-normal text-[color:var(--muted)]"> — optional, or we grab a frame from the video</span>
                </label>
                <div className="flex items-center gap-3">
                  {pubThumb ? (
                    <>
                      <img
                        src={URL.createObjectURL(pubThumb)}
                        alt="Thumbnail preview"
                        className="h-16 w-28 object-cover rounded-lg border border-white/15"
                        data-testid="publish-thumb-preview"
                      />
                      <button
                        onClick={() => setPubThumb(null)}
                        className="text-xs text-[color:var(--muted)] hover:text-red-300"
                        data-testid="publish-thumb-remove"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <label
                      className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border border-dashed border-white/20 text-[color:var(--muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] cursor-pointer transition-colors"
                      data-testid="publish-thumb-pick"
                    >
                      Choose image…
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => setPubThumb(e.target.files?.[0] || null)}
                        data-testid="publish-thumb-input"
                      />
                    </label>
                  )}
                </div>
              </div>
              <button
                onClick={async () => {
                  if (!publishTitle.trim()) {
                    setStitchError("Please give the documentary a title first.");
                    return;
                  }
                  setPublishing(true);
                  setStitchError("");
                  try {
                    const stitchJobId = stitchResult.output_filename
                      ? stitchResult.output_filename
                          .replace(/^stitched_/, "")
                          .replace(/\.mp4$/, "")
                      : null;
                    if (!stitchJobId) throw new Error("Missing stitch job id");
                    const fd = new FormData();
                    fd.append("job_id", stitchJobId);
                    fd.append("title", publishTitle.trim());
                    fd.append("language", publishLanguage);
                    fd.append("script", script.trim());
                    if (pubMainCat) fd.append("main_category", pubMainCat);
                    if (pubCountry.trim()) fd.append("country", pubCountry.trim());
                    if (pubYear.trim()) fd.append("year", pubYear.trim());
                    if (pubLength) fd.append("story_length", pubLength);
                    if (pubTags.trim()) fd.append("tags", pubTags.trim());
                    if (pubLabels.length > 0) fd.append("labels", pubLabels.join(","));
                    if (pubThumb) fd.append("thumbnail", pubThumb);
                    const { data } = await axios.post(
                      `${API}/multi-part/publish`,
                      fd,
                      { timeout: 120_000 }
                    );
                    // Publish now runs server-side in the background —
                    // poll until it lands (big files can take minutes).
                    const pubId = data.publish_id;
                    if (!pubId) { setPublished(data); return; }
                    setPublishStage("queued");
                    const started = Date.now();
                    while (Date.now() - started < 30 * 60_000) {
                      await new Promise((r) => setTimeout(r, 4000));
                      try {
                        const { data: st } = await axios.get(
                          `${API}/multi-part/publish/${pubId}`, { timeout: 20_000 });
                        setPublishStage(st.stage || "working");
                        setPublishBytes(st.progress_bytes || 0);
                        if (st.status === "done") { setPublished(st.result); return; }
                        if (st.status === "failed") throw new Error(st.error || "Publish failed");
                      } catch (pe) {
                        if (pe?.response?.status === 404 || pe.message?.includes("Publish failed")) throw pe;
                        // transient poll error (gateway blip) — keep going
                      }
                    }
                    throw new Error("Publish is taking unusually long — check My Productions in a few minutes; it may still complete.");
                  } catch (e) {
                    setStitchError(e?.response?.data?.detail || e.message);
                  } finally {
                    setPublishing(false);
                    setPublishStage("");
                  }
                }}
                disabled={publishing || !publishTitle.trim()}
                className="btn-primary text-xs inline-flex items-center gap-2 px-4 py-2 disabled:opacity-40"
                data-testid="publish-to-library"
              >
                {publishing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {publishStage === "fetching_from_storage"
                      ? "Fetching file from storage…"
                      : publishStage === "uploading_to_library"
                        ? `Uploading to library${publishBytes ? ` — ${(publishBytes / 1e6).toFixed(0)} MB sent` : ""} (big files take a few minutes)…`
                        : publishStage === "resuming_after_restart"
                          ? "Server restarted — resuming upload…"
                          : "Publishing…"}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    Publish to library
                  </>
                )}
              </button>
            </div>
          ) : (
            <div
              className="mt-2 mb-4 panel p-4 border-emerald-500/40 bg-emerald-500/[0.06]"
              data-testid="publish-success"
            >
              <div className="text-sm text-emerald-300 font-medium mb-1">
                ✓ Published to your Storylo library
              </div>
              <div className="text-xs text-[color:var(--muted)]">
                RSS feed + Spotify will pick it up at the next feed poll.
              </div>
              <a
                href={published.public_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--accent)] hover:underline"
                data-testid="publish-open-link"
              >
                Open the public page <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <a
              href={`${BACKEND_URL}${stitchResult.output_url}`}
              download
              className="text-xs inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
              data-testid="stitch-download"
            >
              <Download className="w-3.5 h-3.5" />
              Download MP4
            </a>
            <button
              onClick={startNewProject}
              className="text-xs inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors"
              data-testid="stitch-new-project"
            >
              Start a new documentary
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
